import { useState, useEffect, useCallback } from 'react'
import type { ConversationPlan } from '@knownow/domain'
import { supabase } from '@/lib/supabase'
import { getPlanById } from '@/lib/plan-api-client'

export interface UseRealtimePlanResult {
  readonly plan: ConversationPlan | null
  readonly loading: boolean
}

export function useRealtimePlan(planId: string | null): UseRealtimePlanResult {
  const [plan, setPlan] = useState<ConversationPlan | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  const fetchPlan = useCallback(async (id: string): Promise<void> => {
    const next = await getPlanById(id)
    setPlan(next)
    setLoading(false)
  }, [])

  useEffect(() => {
    if (!planId) {
      setLoading(false)
      return
    }

    void fetchPlan(planId)

    const refetch = (): void => {
      void fetchPlan(planId)
    }

    const planChannel = supabase
      .channel(`plan:${planId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_plans',
          filter: `id=eq.${planId}`,
        },
        refetch,
      )
      .subscribe()

    const needsChannel = supabase
      .channel(`needs:${planId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'information_needs',
          filter: `plan_id=eq.${planId}`,
        },
        refetch,
      )
      .subscribe()

    const strategyChannel = supabase
      .channel(`strategy:${planId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_strategies',
          filter: `plan_id=eq.${planId}`,
        },
        refetch,
      )
      .subscribe()

    return (): void => {
      void supabase.removeChannel(planChannel)
      void supabase.removeChannel(needsChannel)
      void supabase.removeChannel(strategyChannel)
    }
  }, [planId, fetchPlan])

  return { plan, loading }
}
